import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import { Button } from "@braintrust/braintrust-ui-components";
import { ArrowTopRightIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useGetActiveScreeningQuery } from "@js/apps/auth/api";
import { useAccountType } from "@js/apps/common/hooks/use-account-type";
import { useUser } from "@js/apps/common/hooks/use-user";
import {
  openIdentityVerificationModal,
  useUserNeedsIdentityVerification,
} from "@js/apps/identity-verification";

type PendingFreelancerActionButtonProps = ButtonProps & {
  withIcon?: boolean;
};

export const PendingFreelancerActionButton = (
  props: PendingFreelancerActionButtonProps,
) => {
  const { userNeedsIdentityVerification } = useUserNeedsIdentityVerification();

  if (userNeedsIdentityVerification) {
    return <StartIdentityVerificationButton {...props} />;
  }

  return <StartScreeningButton {...props} />;
};

export const StartIdentityVerificationButton = (
  props: PendingFreelancerActionButtonProps,
) => {
  const onVerifyIdentityClick = () => {
    openIdentityVerificationModal();
  };

  return (
    <PendingFreelancerActionButtonBase
      label={"Verify Identity"}
      {...props}
      onClick={() => {
        onVerifyIdentityClick();
      }}
    />
  );
};

export const StartScreeningButton = (
  props: PendingFreelancerActionButtonProps,
) => {
  const { isFreelancerApproved, isEmployer, isUserVerified } = useAccountType();
  const { data: screening } = useGetActiveScreeningQuery(undefined, {
    skip: isFreelancerApproved || isEmployer || !isUserVerified,
  });
  const screeningUrl = screening?.screening_url;
  const isRetake = screening?.is_retry_attempt;
  const user = useUser();

  if (!screeningUrl || !user || !user.freelancer) return null;

  return (
    <PendingFreelancerActionButtonBase
      label={isRetake ? "Retake Screening" : "Start Screening"}
      {...props}
      onClick={() => {
        window.location.href = screeningUrl;
      }}
    />
  );
};

type PendingFreelancerActionButtonBaseProps =
  PendingFreelancerActionButtonProps & {
    label: string;
  };

export const PendingFreelancerActionButtonBase = ({
  label,
  withIcon,
  ...props
}: PendingFreelancerActionButtonBaseProps) => {
  return (
    <Button
      {...props}
      sx={{
        ...props.sx,
        flexShrink: 0,
        height: { sm: 40 },
      }}
    >
      {label}
      {withIcon && (
        <ArrowTopRightIcon
          style={{
            fontSize: props.size === "x-small" ? 16 : 24,
            marginLeft: 8,
          }}
        />
      )}
    </Button>
  );
};
